import { useState } from 'react'
import { Avatar } from '../../components/index.js'
import { apiFetch } from '../../lib/api-client.js'
import { useChatStore } from '../../stores/useChatStore.js'
import type { Chat } from '../../types/chat.js'
import type { UserProfile } from '../../types/user.js'

interface CreateGroupPanelProps {
  users: UserProfile[]
  currentUser: UserProfile
  onCreated: (chatId: string) => void
  onClose: () => void
}

export function CreateGroupPanel({ users, currentUser, onCreated, onClose }: CreateGroupPanelProps) {
  const [search, setSearch] = useState('')
  const [groupName, setGroupName] = useState('')
  const [selectedMembers, setSelectedMembers] = useState<number[]>([])
  const [isCreating, setIsCreating] = useState(false)
  const addChat = useChatStore((s) => s.addChat)

  const otherUsers = users.filter((u) => u.id !== currentUser.id)
  const filtered = otherUsers.filter(
    (u) => !search || u.name.toLowerCase().includes(search.toLowerCase()),
  )

  function toggleMember(userId: number) {
    setSelectedMembers((prev) =>
      prev.includes(userId) ? prev.filter((id) => id !== userId) : [...prev, userId],
    )
  }

  async function handleCreate() {
    if (!groupName.trim() || selectedMembers.length === 0 || isCreating) return
    setIsCreating(true)
    try {
      const chat = await apiFetch<Chat>('/api/internal-chats', {
        method: 'POST',
        body: JSON.stringify({
          type: 'group',
          name: groupName.trim(),
          memberIds: selectedMembers,
        }),
      })
      addChat(chat)
      onCreated(chat.id)
    } catch (err) {
      console.error('[ChatInterno] Erro ao criar grupo:', err)
    } finally {
      setIsCreating(false)
    }
  }

  return (
    <div className="flex h-full flex-col">
      {/* Header */}
      <div className="flex items-center gap-3 border-b border-(--color-border) px-4 py-3">
        <button
          onClick={onClose}
          className="flex h-8 w-8 items-center justify-center rounded-md text-(--color-text-secondary) hover:bg-(--color-bg-tertiary) transition-colors"
        >
          <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        <h3 className="text-sm font-semibold text-(--color-text-primary)">Criar grupo</h3>
      </div>

      {/* Group name */}
      <div className="border-b border-(--color-border) px-4 py-3">
        <input
          type="text"
          value={groupName}
          onChange={(e) => setGroupName(e.target.value)}
          placeholder="Nome do grupo..."
          autoFocus
          className="w-full rounded-md border border-(--color-border) bg-(--color-bg-secondary) px-3 py-2 text-sm text-(--color-text-primary) placeholder:text-(--color-text-muted) focus:border-(--color-primary) focus:outline-none"
        />
      </div>

      {/* Search members */}
      <div className="border-b border-(--color-border) px-4 py-2">
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Buscar membros..."
          className="w-full rounded-md border border-(--color-border) bg-(--color-bg-secondary) px-3 py-2 text-sm text-(--color-text-primary) placeholder:text-(--color-text-muted) focus:border-(--color-primary) focus:outline-none"
        />
      </div>

      {/* Selected count + create button */}
      <div className="flex items-center justify-between border-b border-(--color-border) px-4 py-2">
        <span className="text-xs text-(--color-text-muted)">
          {selectedMembers.length} membro{selectedMembers.length !== 1 ? 's' : ''} selecionado{selectedMembers.length !== 1 ? 's' : ''}
        </span>
        <button
          onClick={handleCreate}
          disabled={!groupName.trim() || selectedMembers.length === 0 || isCreating}
          className="rounded-md bg-(--color-primary) px-3 py-1.5 text-xs font-medium text-white hover:bg-(--color-primary-dark) transition-colors disabled:opacity-50"
        >
          {isCreating ? 'Criando...' : 'Criar grupo'}
        </button>
      </div>

      {/* Member selection list */}
      <div className="flex-1 overflow-y-auto">
        {filtered.map((u) => {
          const isSelected = selectedMembers.includes(u.id)
          return (
            <button
              key={u.id}
              onClick={() => toggleMember(u.id)}
              className={`flex w-full items-center gap-3 px-4 py-3 text-left transition-colors ${
                isSelected ? 'bg-(--color-primary-light) dark:bg-(--color-primary)/10' : 'hover:bg-(--color-bg-tertiary)'
              }`}
            >
              <Avatar name={u.name} avatarUrl={u.avatarUrl} size="md" />
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-(--color-text-primary)">{u.name}</p>
                <p className="text-xs text-(--color-text-muted)">
                  {u.role === 'administrator' ? 'Administrador' : 'Agente'}
                </p>
              </div>
              <div
                className={`flex h-5 w-5 shrink-0 items-center justify-center rounded border transition-colors ${
                  isSelected
                    ? 'border-(--color-primary) bg-(--color-primary) text-white'
                    : 'border-(--color-border)'
                }`}
              >
                {isSelected && (
                  <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                )}
              </div>
            </button>
          )
        })}
      </div>
    </div>
  )
}
